import * as BikeApi from "features/bike/api"
import { compareIntersectionEgalityOf2Objects, IS_FNUCI_SYNC, PRIVATE_GENDER_VALUE } from "utils"
import { BikeRegistrationActionType } from "features/dashboard/hooks/useBikeRegistrationProcess"
import {
	DefaultStateProps,
	BikeRegistrationAction,
} from "features/dashboard/hooks/useBikeRegistrationProcess"
import { User } from "models"
import { ACTIVATION_CODE_TYPE } from "./CodeValidationForm"

const FNUCI_PRIVATE_FIELDS_LIST = ["gender", "last_name", "first_name", "phone", "mail"]
const FNUCI_COMPANY_FIELDS_LIST = ["gender", "social_reason", "phone", "mail"]

type FinalizeBikeRegistrationProps = {
	code: string
	codeType: string
	bike: DefaultStateProps
	user: User
	bikeDispatch: React.Dispatch<BikeRegistrationAction>
	refreshBikes: () => Promise<void>
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const finalizeBikeRegistration = async ({
	code,
	codeType,
	bike,
	user,
	bikeDispatch,
	refreshBikes,
	setModalIsOpen,
}: FinalizeBikeRegistrationProps) => {
	const addBikePayload = {
		bike_id: bike?.bikeId,
		...(() => {
			if (codeType === ACTIVATION_CODE_TYPE) {
				return {
					code: code,
				}
			} else {
				return {
					password: code,
				}
			}
		})(),
	}

	// 1. Send the couple code / bike_id to the backend
	// ------------------------
	const { res: confirmAddBikeInfo, getError: getConfirmAddBikeError } =
		await BikeApi.confirmAddBike(addBikePayload)

	if (getConfirmAddBikeError()) throw getConfirmAddBikeError()

	if (!confirmAddBikeInfo?.bike_id) throw getConfirmAddBikeError()

	// 2. Get the corresponding bike
	// ------------------------
	const { res: correspondingBike, getError: getBikeError } = await BikeApi.getBike(
		confirmAddBikeInfo.bike_id
	)
	if (getBikeError()) throw getBikeError()

	// 3. Check if the bike is already sync in the FNUCI
	// ------------------------
	// If the bike is not sync, redirect to the register bike page
	if (correspondingBike.is_sync_with_fnuci !== IS_FNUCI_SYNC) {
		bikeDispatch({
			type: BikeRegistrationActionType.BIKE_REGISTRATION_STEP,
			bikeId: bike?.bikeId,
			bikeInfo: null,
			bike: correspondingBike,
		})
	} else {
		// SYNC with FNUCI

		// 4. Check if the owner should be updated
		// ------------------------
		// We compare the current user (user.description) and the bike owner (correspondingBike.bike_owner)
		// (the comparison is only made on essential fields, saved above in corresponding arrays)
		const isBikeOwnerDifferent = compareIntersectionEgalityOf2Objects(
			user.description,
			correspondingBike.bike_owner,
			user.description.gender === PRIVATE_GENDER_VALUE
				? FNUCI_PRIVATE_FIELDS_LIST
				: FNUCI_COMPANY_FIELDS_LIST
		)
		if (isBikeOwnerDifferent) {
			// If differences, we update the fnuci with the bike owner information
			const bikeOwner = {
				gender: user.description.gender,
				phone: user.description.phone,
				mail: user.description.mail,
				...(() => {
					if (user.description.gender === PRIVATE_GENDER_VALUE) {
						return {
							first_name: user.description.first_name,
							last_name: user.description.last_name,
						}
					} else {
						return {
							social_reason: user.description.social_reason,
						}
					}
				})(),
			}

			const updatedBikePayload = {
				bike_id: correspondingBike.bike_id,
				row_version: correspondingBike.row_version,
				bike_owner: bikeOwner,
			}

			const { getError: getUpdateBikeError } = await BikeApi.updateBike(updatedBikePayload)
			if (getUpdateBikeError()) throw getUpdateBikeError()
		}

		// Return to initial step
		bikeDispatch({
			type: BikeRegistrationActionType.INITIAL_STEP,
		})
		setModalIsOpen(false)

		await refreshBikes()
	}
}
